/**
 * Analytics — Activity Feed API Contracts
 *
 * Wire format for ActivityFeedController endpoints.
 */

import { z } from 'zod';

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Request schemas
// ---------------------------------------------------------------------------

export const ListActivitiesQuerySchema = z.object({
  entityType: z.string().optional(),
  actorId: z.string().optional(),
  action: z.string().optional(),
  dateFrom: z.string().regex(DATE_REGEX).optional(),
  dateTo: z.string().regex(DATE_REGEX).optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListActivitiesQuery = z.infer<typeof ListActivitiesQuerySchema>;

export const CreateActivityRequestSchema = z.object({
  actorId: z.string().min(1),
  actorName: z.string().min(1),
  actorRole: z.string().min(1),
  action: z.string().min(1),
  entityType: z.string().min(1),
  entityId: z.string().optional(),
  description: z.string().min(1),
  metadata: z.record(z.unknown()).optional(),
});
export type CreateActivityRequest = z.infer<typeof CreateActivityRequestSchema>;

// ---------------------------------------------------------------------------
// Response schemas
// ---------------------------------------------------------------------------

export const ActivityRecordSchema = z.object({
  id: z.string(),
  actorId: z.string(),
  actorName: z.string(),
  actorRole: z.string(),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable().optional(),
  description: z.string(),
  metadata: z.unknown().nullable().optional(),
  createdAt: z.string(),
});
export type ActivityRecord = z.infer<typeof ActivityRecordSchema>;

export const ListActivitiesResponseSchema = z.object({
  activities: z.array(ActivityRecordSchema),
  total: z.number().int(),
  page: z.number().int(),
  limit: z.number().int(),
  totalPages: z.number().int(),
});
export type ListActivitiesResponse = z.infer<typeof ListActivitiesResponseSchema>;

export const ActivitySummaryResponseSchema = z.object({
  period: z.string(),
  since: z.string(),
  totalActions: z.number().int(),
  byAction: z.array(z.object({
    action: z.string(),
    count: z.number().int(),
  })),
});
export type ActivitySummaryResponse = z.infer<typeof ActivitySummaryResponseSchema>;
